import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, X, Zap, Image, PenTool, Sparkles, BrainCircuit } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { AIIntegration, StoredAIIntegration } from '@/types/banner';
import { saveAIIntegrationSettings, loadAIIntegrationSettings } from '@/utils/ai/aiIntegrationManager';

interface AISettingsTabProps {
  onSaved: () => void;
}

const AISettingsTab: React.FC<AISettingsTabProps> = ({ onSaved }) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({
    '1': '',
    '2': '',
    '3': '',
    '4': '',
  });

  // Sample AI integrations 
  const [integrations, setIntegrations] = useState<AIIntegration[]>([
    {
      id: '1',
      name: 'OpenAI',
      description: 'Text and image generation with GPT-4 and DALL-E models',
      logo: '/lovable-uploads/5705fcb9-6fed-45b6-a4ba-8f777b4ef6ce.png',
      isConnected: false,
    },
    {
      id: '2',
      name: 'Midjourney',
      description: 'High-quality AI image generation for banner creation',
      logo: '/lovable-uploads/843f56a9-ea90-4bfc-9000-a1b7e1408a94.png',
      isConnected: false,
    },
    {
      id: '3',
      name: 'Stability AI',
      description: 'Advanced image generation with Stable Diffusion models',
      logo: '/lovable-uploads/5df22fbf-46c0-422b-a964-30ad729c926d.png',
      isConnected: false,
    },
    {
      id: '4',
      name: 'Hugging Face',
      description: 'Open-source AI models for text, image, and audio processing',
      logo: '/lovable-uploads/74192c5a-ba76-4e08-9c4a-f98e75942d6c.png',
      isConnected: false,
    }
  ]);

  // Load saved API keys and connection status from localStorage
  useEffect(() => {
    const savedSettings = loadAIIntegrationSettings();
    if (savedSettings && savedSettings.length > 0) {
      // Update apiKeys state
      const loadedApiKeys: Record<string, string> = {};
      savedSettings.forEach(setting => {
        if (setting.apiKey) {
          loadedApiKeys[setting.id] = setting.apiKey;
        }
      });
      
      setApiKeys(prevKeys => ({
        ...prevKeys,
        ...loadedApiKeys
      }));
      
      // Update integrations state with connection status
      setIntegrations(prevIntegrations => 
        prevIntegrations.map(integration => {
          const savedIntegration = savedSettings.find(s => s.id === integration.id);
          if (savedIntegration) {
            return {
              ...integration,
              isConnected: savedIntegration.isConnected,
              apiKey: savedIntegration.apiKey,
              lastUsed: savedIntegration.lastUsed ? new Date(savedIntegration.lastUsed) : undefined
            };
          }
          return integration;
        })
      );
    }
  }, []);

  const handleApiKeyChange = (id: string, value: string) => {
    setApiKeys(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleConnectIntegration = async (id: string) => {
    if (!apiKeys[id]?.trim()) {
      toast({
        title: "API key required",
        description: "Please enter an API key to connect this service.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Update integration status in state
      const updatedIntegrations = integrations.map(integration => 
        integration.id === id ? { 
          ...integration, 
          isConnected: true,
          apiKey: apiKeys[id],
          lastUsed: new Date()
        } : integration
      );
      
      setIntegrations(updatedIntegrations);
      
      // Save to localStorage
      saveAIIntegrationSettings(updatedIntegrations);
      
      toast({
        title: "Integration connected",
        description: "AI service has been successfully connected.",
      });
      
      onSaved();
    } catch (error) {
      toast({
        title: "Connection failed",
        description: "Could not connect to the AI service.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisconnectIntegration = async (id: string) => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Update integration status
      const updatedIntegrations = integrations.map(integration => 
        integration.id === id ? { ...integration, isConnected: false } : integration
      );
      
      setIntegrations(updatedIntegrations);

      // Save to localStorage
      saveAIIntegrationSettings(updatedIntegrations);
      
      toast({
        title: "Integration disconnected",
        description: "AI service has been disconnected.",
      });
      
      onSaved();
    } catch (error) {
      toast({
        title: "Disconnection failed",
        description: "Could not disconnect from the AI service.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">AI Integration Settings</h2>
      <p className="text-muted-foreground">
        Connect to AI services to enable advanced features like banner generation and content creation.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
        {integrations.map((integration) => (
          <Card key={integration.id} className="overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  {integration.name === 'OpenAI' && <Sparkles className="h-5 w-5 text-primary" />}
                  {integration.name === 'Midjourney' && <Image className="h-5 w-5 text-primary" />}
                  {integration.name === 'Stability AI' && <BrainCircuit className="h-5 w-5 text-primary" />}
                  {integration.name === 'Hugging Face' && <PenTool className="h-5 w-5 text-primary" />}
                </div>
                <div>
                  <CardTitle className="text-lg">{integration.name}</CardTitle>
                  <CardDescription className="text-xs">{integration.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pb-2">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor={`${integration.id}-api-key`}>API Key</Label>
                  <Input
                    id={`${integration.id}-api-key`}
                    type="password"
                    placeholder="Enter API key"
                    value={apiKeys[integration.id] || ''}
                    onChange={(e) => handleApiKeyChange(integration.id, e.target.value)}
                    disabled={integration.isConnected}
                  />
                  <p className="text-xs text-muted-foreground">
                    API key for {integration.name} integration
                  </p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col items-stretch space-y-3 pt-2">
              {integration.isConnected ? (
                <Button
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => handleDisconnectIntegration(integration.id)}
                  disabled={isSaving}
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => handleConnectIntegration(integration.id)}
                  disabled={isSaving}
                >
                  {isSaving ? "Connecting..." : "Connect"}
                </Button>
              )}
              
              <Button 
                variant={integration.isConnected ? "success" : "outline"} 
                className={`w-full ${integration.isConnected ? "bg-green-500 hover:bg-green-600" : "bg-gray-200 text-gray-700"} flex justify-center items-center gap-2`}
                disabled={true} // This button is just a status indicator
              >
                {integration.isConnected ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Live</span>
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4" />
                    <span>Not Connected</span>
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mt-6">
        <h3 className="text-md font-medium text-blue-800 flex items-center">
          <Zap className="h-5 w-5 mr-2" />
          AI Features
        </h3>
        <ul className="mt-2 space-y-1 text-sm text-blue-700">
          <li className="flex items-start">
            <Check className="h-4 w-4 mr-2 mt-0.5 text-green-500" />
            AI-powered banner text generation
          </li>
          <li className="flex items-start">
            <Check className="h-4 w-4 mr-2 mt-0.5 text-green-500" />
            Image generation for marketing materials
          </li>
          <li className="flex items-start">
            <Check className="h-4 w-4 mr-2 mt-0.5 text-green-500" />
            Content optimization suggestions
          </li>
          <li className="flex items-start">
            <Check className="h-4 w-4 mr-2 mt-0.5 text-green-500" />
            Open-source model support via Hugging Face
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AISettingsTab;
